import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { CalendarLinkModal } from '../MyProgramsPage';

export type EpNavKey = 'tasks' | 'list' | 'calendar' | 'find';
const VIEW_KEY = 'ep.myprograms.view';
/** The counselor and parent Programs tab names its views tasks | theirs | find (?ep= on the student page). */
const VIEWER_TAB: Record<string, string> = { tasks: 'tasks', list: 'theirs', find: 'find' };

/**
 * The same quick buttons at the top of every Educational Programs page.
 * Student: All tasks, My Programs, Calendar, Find programs, Add to my calendar.
 * Counselor or parent looking at a student (forStudent): All tasks, <Name>'s Programs,
 * Find programs, Add <Name>'s dates to my calendar. On the page that owns the views,
 * onView switches in place; on other pages the buttons open that page on the view.
 */
export default function EpNav({ active, studentId, badge = 0, onView, forStudent }: {
  active: EpNavKey | null;
  studentId: number;
  badge?: number;
  onView?: (k: EpNavKey) => void;
  forStudent?: { name: string };
}) {
  const history = useHistory();
  const [calOpen, setCalOpen] = useState(false);
  const name = forStudent?.name || 'the student';
  const views: [EpNavKey, string][] = forStudent
    ? [['tasks', 'All tasks'], ['list', `${name}'s Programs`], ['find', 'Find programs']]
    : [['tasks', 'All tasks'], ['list', 'My Programs'], ['calendar', 'Calendar']];
  const go = (k: EpNavKey) => {
    if (onView) {
      onView(k);
      return;
    }
    if (forStudent) {
      history.push(`/application/counselor/${studentId}?tab=11&ep=${VIEWER_TAB[k] ?? 'tasks'}`);
      return;
    }
    if (k === 'find') {
      history.push('/summer-programs');
      return;
    }
    try {
      localStorage.setItem(VIEW_KEY, JSON.stringify({ v: k }));
    } catch {
      /* storage blocked: My Programs opens on its default view */
    }
    history.push('/summer-programs/manager');
  };
  const btn = (k: EpNavKey, l: string) => (
    <button key={k} type="button" className={`chipb ${active === k ? 'on' : ''}`} aria-current={active === k ? 'page' : undefined} onClick={() => go(k)}>
      {l}
      {k === 'list' && badge > 0 && <span className="ep-badge" aria-label={`${badge} new suggestions`}>{badge}</span>}
    </button>
  );
  return (
    <nav className="row ep-nav" aria-label="Educational Programs">
      <div className="row" role="group" aria-label="Views">
        {views.map(([k, l]) => btn(k, l))}
      </div>
      {!forStudent && (
        <button type="button" className={`btn ${active === 'find' ? 'on' : ''}`} aria-current={active === 'find' ? 'page' : undefined} onClick={() => go('find')}>Find programs</button>
      )}
      {forStudent && <span className="ep-nav-gap" />}
      <button type="button" className="btn" onClick={() => setCalOpen(true)}>{forStudent ? `Add ${name}'s dates to my calendar` : 'Add to my calendar'}</button>
      {calOpen && <CalendarLinkModal studentId={studentId} studentName={forStudent ? name : undefined} onClose={() => setCalOpen(false)} />}
    </nav>
  );
}
